import React, { useState } from "react";
import { Ticket } from "../../../../../models/Ticket";
import { useOtherAccountDisplayName } from "../../../../../../utils/hooks/useOtherAccountDisplayName";
import EAInfoTile from "../../../../shared/InfoTile/EAInfoTile";
import { differenceInDays, format } from "date-fns";
import PassTag from "./PassTag";
import "./SinglePass.scss";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import {
  getDateLabel,
  getEntranceInfo,
  isTicketInactive,
  getInfoTileIconType,
  getActionButtonLabel,
  getActionButtonVariant,
  ICON_COMPONENTS,
  isTicketType,
  isTicketExpired,
  getInfoTileColorForExpired,
  isPassType,
  PassType,
  hasEntranceWarning,
  isTicketUsedUp,
  isSwimmingSchool,
} from "../../../../../utils/TicketPassHelper";
import TicketDetailsModal from "../PassModal/TicketDetailsModal";
import PassDetailsModal from "../PassModal/PassDetailsModal";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAIconButton from "../../../../shared/Buttons/EAIconButton";
import { MdChevronRight } from "react-icons/md";

interface IProps {
  pass: Ticket;
  onRefreshTickets?: () => void;
  tempNotice?: string; // TEMP_PASS_NOTICES
}

const SinglePass: React.FC<IProps> = ({ pass, onRefreshTickets, tempNotice }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const isDesktop = useDesktopSize();
  const otherAccountName = useOtherAccountDisplayName(pass.accountIds);

  // Sprawdzenie czy bilet jest wygasły
  const isExpired = isTicketExpired(pass);

  // Sprawdzenie czy bilet jest nieaktywny (brak wybranego terminu)
  // LIMITED bilety nigdy nie będą nieaktywne - to jest obsługiwane w isTicketInactive!
  const isInactive = !isExpired && isTicketInactive(pass);

  // Sprawdź czy to jest karnet zajęciowy (passtype 2, ticket false)
  const isClassPassCard = isPassType(pass) && pass.passtype === PassType.CLASS_PASS_SINGLE;

  // Sprawdzenie czy kończą się wejścia
  const isUsed = !isExpired && isTicketUsedUp(pass);
  const hasLowEntrances = !isExpired && !isUsed && hasEntranceWarning(pass);

  // Dla karnetu zajęciowego bez terminu używamy ostrzeżenia (żółty), nie błędu (czerwony)
  const showAsError = isInactive && !isClassPassCard;
  const showAsWarning = isInactive && isClassPassCard;

  // Sprawdzenie czy bilet wkrótce traci ważność (7 dni lub mniej)
  const getDaysUntilExpiration = () => {
    const validToDate = typeof pass.validto === "number" ? new Date(pass.validto) : new Date(pass.validto);
    return differenceInDays(validToDate, new Date());
  };

  const daysUntilExpiration = getDaysUntilExpiration();
  const isExpiringsoon = !isExpired && daysUntilExpiration >= 0 && daysUntilExpiration <= 7;

  const IconComponent = ICON_COMPONENTS[getInfoTileIconType(pass)];
  const infoTileColor = getInfoTileColorForExpired(pass, isExpired);
  const buttonLabel = getActionButtonLabel(pass);
  const buttonVariant = getActionButtonVariant(pass);

  const handleActionClick = () => {
    setOpenDetails(true);
  };

  const handleCloseModal = () => {
    setOpenDetails(false);
    onRefreshTickets?.();
  };

  return (
    <>
      <div
        className={`single-pass__container ${
          showAsError && !isExpired && !isUsed ? "single-pass__container--inactive" : ""
        } ${
          (isExpiringsoon || showAsWarning || hasLowEntrances) && !showAsError && !isExpired && !isUsed
            ? "single-pass__container--expiring-soon"
            : ""
        } ${isExpired ? "single-pass__container--expired" : ""} ${isUsed ? "single-pass__container--used" : ""} ${
          !isDesktop ? "single-pass__container--mobile-clickable" : ""
        }`}
        onClick={!isDesktop ? handleActionClick : undefined}
      >
        <div className="single-pass__left-col">
          {isDesktop && <EAInfoTile icon={<IconComponent />} color={infoTileColor} />}
          <div className="single-pass__left-col-content">
            <PassTag
              pass={pass}
              isInactive={isInactive}
              isExpiringsoon={isExpiringsoon}
              isExpired={isExpired}
              isUsed={isUsed}
              tempNotice={tempNotice}
            />
            <h5>{pass.name}</h5>
            <span className={`body-text-small ${isExpired || isUsed ? "text-color-light-gray" : "text-color-dark-gray"}`}>
              {getDateLabel(pass)} {format(new Date(pass.validto), "dd.MM.yyyy")}
              {getEntranceInfo(pass) ? ` • ${getEntranceInfo(pass)}` : ""}
              {otherAccountName ? ` • ${otherAccountName}` : ""}
            </span>
          </div>
        </div>

        <div className="single-pass__right-col">
          {isDesktop ? (
            <EAButton title={buttonLabel} variant={buttonVariant} onClick={handleActionClick} />
          ) : (
            <EAIconButton
              title={buttonLabel}
              icon={<MdChevronRight />}
              variant="icon"
              color="primary"
              onClick={handleActionClick}
            />
          )}
        </div>
      </div>

      {isTicketType(pass) && !isSwimmingSchool(pass.passtype) ? (
        <TicketDetailsModal
          isOpen={openDetails}
          onClose={handleCloseModal}
          ticket={pass}
          onRefreshTickets={onRefreshTickets}
          tempNotice={tempNotice}
        />
      ) : (
        <PassDetailsModal
          isOpen={openDetails}
          onClose={handleCloseModal}
          pass={pass}
          onRefreshTickets={onRefreshTickets}
          tempNotice={tempNotice}
        />
      )}
    </>
  );
};

export default SinglePass;
